// delegate-shell-theme
// Pure contract for the Delegate V2 shell -> DelegateMeet theme bridge.
// Ported from DelegateProject (gitea/master apps/web/src/lib/delegate-shell)
// with the Meet ready type and token map. Spec: docs/delegate/THEME.md (v2,
// backward compatible with v1). No DOM writes in this module.

export const THEME_READY_TYPE = "delegate-meet:theme-ready" as const;
export const SHELL_THEME_TYPE = "delegate-shell:theme" as const;

/** Section 1.5: every contract version Meet accepts, ascending. */
export const SUPPORTED_VERSIONS = [1, 2] as const;
export type ShellThemeVersion = (typeof SUPPORTED_VERSIONS)[number];
/** The highest version Meet supports (sent as `theme-ready.version`). */
export const SHELL_THEME_VERSION = 2 as const;

/** Section 9.3: the v2 cache key. The v1 key is removed on start. */
export const SHELL_THEME_CACHE_KEY = "delegate-shell-theme:v2";
export const LEGACY_SHELL_THEME_CACHE_KEYS = ["delegate-shell-theme:v1"] as const;

/** Section 5.1 structure limits, per version. */
export const LIMITS = Object.freeze({
  1: Object.freeze({ message: 8192, tokens: 64, metrics: 0, fontFaces: 0 }),
  2: Object.freeze({ message: 24576, tokens: 96, metrics: 16, fontFaces: 12 }),
} as const);
/** Kept for v1 callers and tests. */
export const MAX_MESSAGE_LENGTH = LIMITS[1].message;
export const MAX_TOKEN_KEYS = LIMITS[1].tokens;
export const MAX_VALUE_LENGTH = 64;

export type ShellMode = "light" | "dark";

export interface ThemeReadyMessage {
  type: typeof THEME_READY_TYPE;
  version: typeof SHELL_THEME_VERSION;
  versions: ShellThemeVersion[];
}

/** Section 2: the 33 colour token names V2 sent in v1. */
export const V2_TOKEN_NAMES = [
  "--app-canvas",
  "--app-panel",
  "--app-raised",
  "--app-inset",
  "--app-chip",
  "--app-hover",
  "--app-line-subtle",
  "--app-line",
  "--app-line-strong",
  "--app-field",
  "--app-strong",
  "--app-body",
  "--app-muted",
  "--app-faint",
  "--primary",
  "--primary-foreground",
  "--primary-ink",
  "--ring",
  "--destructive",
  "--app-success-ink",
  "--app-success-surface",
  "--app-success-line",
  "--app-warning-ink",
  "--app-warning-surface",
  "--app-warning-line",
  "--app-danger-ink",
  "--app-danger-surface",
  "--app-danger-line",
  "--chart-1",
  "--chart-2",
  "--chart-3",
  "--chart-4",
  "--chart-5",
] as const;

/** Section 2, "Added in this revision": additive, so also sent in v1. */
export const SHELL_EXTRA_COLOUR_TOKENS = [
  "--app-info-ink",
  "--app-info-surface",
  "--app-info-line",
  "--app-note",
  "--app-note-line",
  "--app-note-text",
] as const;

/** Section 2: all 39 colour names, in V2's order. */
export const SHELL_COLOUR_TOKENS = [...V2_TOKEN_NAMES, ...SHELL_EXTRA_COLOUR_TOKENS] as const;

export type V2TokenName = (typeof SHELL_COLOUR_TOKENS)[number];

/**
 * Sections 4.2 (Cal layer) and 4.3 (coss-ui layer): Meet target <- V2 source.
 * Fixed; lives only in Meet. Every target is a runtime custom property in
 * packages/config/theme/tokens.css or packages/coss-ui/src/styles/globals.css.
 */
export const SHELL_TO_MEET_MAP: Readonly<Record<string, V2TokenName>> = Object.freeze({
  // ── 4.2 Cal layer ──
  "--cal-bg": "--app-canvas",
  // ★ V2 rails sit on the canvas (sidebar and top nav).
  "--cal-bg-muted": "--app-canvas",
  // ★ V2 nav-item hover.
  "--cal-bg-subtle": "--app-inset",
  "--cal-bg-emphasis": "--app-chip",
  "--cal-bg-primary": "--primary",
  "--cal-bg-brand": "--primary",
  "--cal-bg-primary-emphasis": "--primary-ink",
  "--cal-bg-brand-emphasis": "--primary-ink",
  "--cal-bg-primary-muted": "--app-chip",
  "--cal-bg-brand-muted": "--app-chip",
  "--cal-brand": "--primary",
  "--cal-brand-emphasis": "--primary-ink",
  "--cal-brand-text": "--primary-foreground",
  "--cal-brand-accent": "--primary-foreground",
  "--cal-brand-subtle": "--app-chip",
  "--cal-border": "--app-line",
  "--cal-border-subtle": "--app-line-subtle",
  "--cal-border-muted": "--app-line-subtle",
  "--cal-border-emphasis": "--app-line-strong",
  "--cal-border-error": "--app-danger-line",
  "--cal-border-semantic-error": "--app-danger-line",
  "--cal-border-semantic-error-subtle": "--app-danger-line",
  "--cal-border-semantic-attention-subtle": "--app-warning-line",
  "--cal-text-emphasis": "--app-strong",
  "--cal-text": "--app-body",
  "--cal-text-subtle": "--app-muted",
  "--cal-text-muted": "--app-faint",
  "--cal-bg-success": "--app-success-surface",
  "--cal-bg-semantic-success-subtle": "--app-success-surface",
  "--cal-text-success": "--app-success-ink",
  "--cal-text-semantic-success": "--app-success-ink",
  "--cal-bg-semantic-success-emphasis": "--app-success-ink",
  "--cal-bg-attention": "--app-warning-surface",
  "--cal-bg-semantic-attention-subtle": "--app-warning-surface",
  "--cal-text-attention": "--app-warning-ink",
  "--cal-text-semantic-attention": "--app-warning-ink",
  "--cal-bg-semantic-attention-emphasis": "--app-warning-ink",
  "--cal-bg-error": "--app-danger-surface",
  "--cal-bg-semantic-error-subtle": "--app-danger-surface",
  "--cal-text-error": "--app-danger-ink",
  "--cal-text-semantic-error": "--app-danger-ink",
  "--cal-bg-semantic-error-emphasis": "--destructive",
  "--cal-bg-info": "--app-info-surface",
  "--cal-bg-semantic-info-subtle": "--app-info-surface",
  "--cal-text-info": "--app-info-ink",
  "--cal-text-semantic-info": "--app-info-ink",
  "--cal-bg-semantic-info-emphasis": "--app-info-ink",
  "--cal-bg-visualization-1-emphasis": "--chart-1",
  "--cal-bg-visualization-2-emphasis": "--chart-2",
  "--cal-bg-visualization-3-emphasis": "--chart-3",
  "--cal-bg-visualization-4-emphasis": "--chart-4",
  "--cal-bg-visualization-5-emphasis": "--chart-5",
  // ── 4.3 coss-ui layer (shadcn names) ──
  "--background": "--app-canvas",
  "--foreground": "--app-body",
  "--card": "--app-panel",
  "--card-foreground": "--app-body",
  "--popover": "--app-raised",
  "--popover-foreground": "--app-body",
  "--primary": "--primary",
  "--primary-foreground": "--primary-foreground",
  "--secondary": "--app-chip",
  "--secondary-foreground": "--app-strong",
  "--muted": "--app-inset",
  "--muted-foreground": "--app-muted",
  "--accent": "--app-hover",
  "--accent-foreground": "--app-strong",
  "--destructive": "--destructive",
  "--destructive-foreground": "--app-danger-ink",
  "--success": "--app-success-ink",
  "--success-foreground": "--app-success-ink",
  "--warning": "--app-warning-ink",
  "--warning-foreground": "--app-warning-ink",
  "--info": "--app-info-ink",
  "--info-foreground": "--app-info-ink",
  "--border": "--app-line",
  "--input": "--app-line-strong",
  "--ring": "--ring",
  "--sidebar-ring": "--ring",
  "--chart-1": "--chart-1",
  "--chart-2": "--chart-2",
  "--chart-3": "--chart-3",
  "--chart-4": "--chart-4",
  "--chart-5": "--chart-5",
  "--sidebar": "--app-canvas",
  "--sidebar-foreground": "--app-body",
  "--sidebar-accent-foreground": "--app-body",
  "--sidebar-primary": "--primary",
  "--sidebar-primary-foreground": "--primary-foreground",
  "--sidebar-accent": "--app-inset",
  "--sidebar-border": "--app-line-subtle",
  "--code": "--app-inset",
  "--code-highlight": "--app-hover",
});

export const MEET_TARGETS: readonly string[] = Object.keys(SHELL_TO_MEET_MAP);

/**
 * Section 4.3: received colour tokens also written under their own name.
 * The name grammar keeps additive `--app-*` names working without letting a
 * payload write arbitrary properties.
 */
const OWN_NAME = /^--app-[a-z0-9]+(?:-[a-z0-9]+){0,4}$/;
export function isOwnNameToken(name: string): boolean {
  return name.length <= 48 && (name === "--primary-ink" || OWN_NAME.test(name));
}

/** Section 3: metric tokens (v2 only). Target is always the same name. */
export type MetricKind = "length" | "shadow";
export interface MetricSpec {
  kind: MetricKind;
  min?: number;
  max?: number;
}
export const SHELL_METRICS: Readonly<Record<string, MetricSpec>> = Object.freeze({
  "--radius": { kind: "length", min: 0, max: 32 },
  "--spacing": { kind: "length", min: 2, max: 8 },
  "--container-narrow": { kind: "length", min: 160, max: 2400 },
  "--container-regular": { kind: "length", min: 160, max: 2400 },
  "--container-wide": { kind: "length", min: 160, max: 2400 },
  "--elev-card": { kind: "shadow" },
  "--elev-popover": { kind: "shadow" },
});
export const METRIC_TARGETS: readonly string[] = Object.keys(SHELL_METRICS);

/** Section 4.5: Meet's own stacks, appended after the shell's families. */
export const MEET_DEFAULT_SANS = "Inter, ui-sans-serif, system-ui, sans-serif";
export const MEET_DEFAULT_HEADING = '"Cal Sans", Inter, ui-sans-serif, sans-serif';
export const MEET_DEFAULT_MONO = "ui-monospace, monospace";
const GENERIC_FAMILIES = new Set([
  "sans-serif",
  "system-ui",
  "ui-sans-serif",
  "monospace",
  "ui-monospace",
  "serif",
]);

// ── Section 5.2: colour ────────────────────────────────────────────────────
const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const FN = /^(?:rgba?|hsla?|hwb|lab|lch|oklab|oklch)\([0-9a-z.%+\-/, ]{1,56}\)$/i;

function cssSupports(property: string, value: string): boolean {
  if (typeof CSS === "undefined" || typeof CSS.supports !== "function") {
    return true;
  }
  return CSS.supports(property, value);
}

/** `isColour` without the CSS.supports step (used inside shadows, and by the shared vectors). */
export function isColourGrammar(v: string): boolean {
  if (typeof v !== "string") return false;
  if (v.length > MAX_VALUE_LENGTH) return false;
  return v === "transparent" || HEX.test(v) || FN.test(v);
}

export function isColorValue(v: string): boolean {
  return isColourGrammar(v) && cssSupports("color", v);
}

// ── Section 5.3: length ────────────────────────────────────────────────────
const LEN = /^(?:0|(?:\d{1,4}(?:\.\d{1,4})?|\.\d{1,4})(?:px|rem|em))$/;
export function lengthToPx(v: string): number {
  return v === "0" ? 0 : Number.parseFloat(v) * (v.endsWith("px") ? 1 : 16);
}
export function isLength(v: string, min: number, max: number): boolean {
  if (typeof v !== "string" || v.length > 16 || !LEN.test(v)) return false;
  const px = lengthToPx(v);
  return px >= min && px <= max;
}

// ── Section 5.4: shadow ────────────────────────────────────────────────────
const SLEN = "-?(?:0|(?:\\d{1,3}(?:\\.\\d{1,4})?|\\.\\d{1,4})(?:px|rem|em))";
const LAYER = new RegExp(`^(?:inset )?${SLEN}(?: ${SLEN}){1,3} (.+)$`);
const SHADOW_FORBIDDEN = /[;{}\\"'<>!]|\/\*|url\(|var\(/i;
export function isShadowGrammar(v: string): boolean {
  if (typeof v !== "string") return false;
  if (v === "none") return true;
  if (v.length > 320 || SHADOW_FORBIDDEN.test(v)) return false;
  const layers = v.split(/,(?![^(]*\))/).map((s) => s.trim().replace(/\s+/g, " "));
  if (layers.length < 1 || layers.length > 4) return false;
  return layers.every((layer) => {
    const m = LAYER.exec(layer);
    return !!m && isColourGrammar(m[1] as string);
  });
}
export function isShadow(v: string): boolean {
  return isShadowGrammar(v) && (v === "none" || cssSupports("box-shadow", v));
}

// ── Section 5.5: font-family list ──────────────────────────────────────────
const FAMILY_ITEM =
  /^(?:"[A-Za-z0-9 ._-]{1,48}"|'[A-Za-z0-9 ._-]{1,48}'|[A-Za-z][A-Za-z0-9_-]{0,47}(?: [A-Za-z][A-Za-z0-9_-]{0,47}){0,3})$/;
const WIDE = /^(?:inherit|initial|unset|revert|revert-layer|default)$/i;
export function isFamilyList(v: string): boolean {
  if (typeof v !== "string" || v.length > 200) return false;
  const items = v.split(",").map((s) => s.trim());
  return items.length >= 1 && items.length <= 6 && items.every((i) => FAMILY_ITEM.test(i) && !WIDE.test(i));
}

/** Shell families without generics, then Meet's own stack (section 4.5). */
export function withMeetFallback(list: string, fallback: string): string | null {
  const specific = list
    .split(",")
    .map((s) => s.trim())
    .filter((s) => !GENERIC_FAMILIES.has(s.toLowerCase()));
  if (specific.length === 0) return null;
  return `${specific.join(", ")}, ${fallback}`;
}

// ── Section 5.6: font features ─────────────────────────────────────────────
const FEAT =
  /^(?:normal|"[a-z0-9]{4}"(?: (?:\d{1,2}|on|off))?(?:, ?"[a-z0-9]{4}"(?: (?:\d{1,2}|on|off))?){0,7})$/;
export function isFeatures(v: string): boolean {
  return typeof v === "string" && v.length <= 160 && FEAT.test(v);
}

// ── Section 5.7: font face ─────────────────────────────────────────────────
const FACE_FAMILY = /^[A-Za-z0-9 ._-]{1,48}$/;
const FONT_PATH = /^\/(?:[A-Za-z0-9._~-]{1,64}\/){0,4}[A-Za-z0-9._~-]{1,128}\.woff2$/;
const WEIGHT = /^[1-9]00(?: [1-9]00)?$/;
const URANGE = /^U\+[0-9A-F?]{1,6}(?:-[0-9A-F]{1,6})?(?:, ?U\+[0-9A-F?]{1,6}(?:-[0-9A-F]{1,6})?){0,63}$/i;

export function isFontSrc(src: string, shellOrigin: string | null): boolean {
  if (typeof src !== "string" || !shellOrigin || src.length > 300) {
    return false;
  }
  let u: URL;
  try {
    u = new URL(src);
  } catch {
    return false;
  }
  return (
    u.href === src &&
    u.protocol === "https:" &&
    u.origin === shellOrigin &&
    !u.username &&
    !u.password &&
    !u.search &&
    !u.hash &&
    FONT_PATH.test(u.pathname)
  );
}

export interface ShellFontFace {
  family: string;
  src: string;
  weight: string;
  style: "normal" | "italic";
  unicodeRange?: string;
}

/** Returns the normalised face, or `null` to drop this entry alone. */
export function validateFontFace(
  entry: Record<string, unknown>,
  shellOrigin: string | null
): ShellFontFace | null {
  const { family, src, weight, style, unicodeRange } = entry;
  if (typeof family !== "string" || !FACE_FAMILY.test(family)) return null;
  if (typeof src !== "string" || !isFontSrc(src, shellOrigin)) return null;
  if (weight !== undefined && (typeof weight !== "string" || !WEIGHT.test(weight))) return null;
  if (style !== undefined && style !== "normal" && style !== "italic") {
    return null;
  }
  if (
    unicodeRange !== undefined &&
    (typeof unicodeRange !== "string" || unicodeRange.length > 1024 || !URANGE.test(unicodeRange))
  ) {
    return null;
  }
  const face: ShellFontFace = {
    family,
    src,
    weight: (weight as string | undefined) ?? "400",
    style: (style as ShellFontFace["style"] | undefined) ?? "normal",
  };
  if (unicodeRange !== undefined) face.unicodeRange = unicodeRange as string;
  return face;
}

// ── Section 5.8: layout ────────────────────────────────────────────────────
export interface EmbedLayout {
  embedded: true;
  host: "window";
  chrome: "shell" | "app";
  nav: "rail" | "app";
  density: "regular" | "compact";
}

export const EMBED_DEFAULT_LAYOUT: Readonly<EmbedLayout> = Object.freeze({
  embedded: true,
  host: "window",
  chrome: "shell",
  nav: "rail",
  density: "regular",
});

/** Each invalid field takes its default; unknown keys are ignored. */
export function validateLayout(raw: unknown): EmbedLayout {
  const l = isPlainObject(raw) ? raw : {};
  return {
    embedded: true,
    host: "window",
    chrome: l.chrome === "shell" || l.chrome === "app" ? l.chrome : "shell",
    nav: l.nav === "rail" || l.nav === "app" ? l.nav : "rail",
    density: l.density === "regular" || l.density === "compact" ? l.density : "regular",
  };
}

// ── Section 5.1: structure ─────────────────────────────────────────────────
function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object") return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function hasOnlyStringValues(obj: Record<string, unknown>): boolean {
  return Object.keys(obj).every((k) => typeof obj[k] === "string");
}

export function isSupportedVersion(v: unknown): v is ShellThemeVersion {
  return (SUPPORTED_VERSIONS as readonly unknown[]).includes(v);
}

export type ShellThemeRejectReason =
  | "shape"
  | "size"
  | "type"
  | "version"
  | "mode"
  | "tokens"
  | "metrics"
  | "typography"
  | "fontFaces"
  | "layout";

/** Section 4.5: the typography variables Meet writes. */
export interface ShellTypography {
  sans: string | null;
  heading: string | null;
  mono: string | null;
  features: string | null;
  smoothing: "antialiased" | "auto" | null;
}

export interface AcceptedShellTheme {
  ok: true;
  version: ShellThemeVersion;
  mode: ShellMode;
  /** Meet target -> accepted colour (sections 4.2 and 4.3). */
  values: Map<string, string>;
  /** Own-name colour tokens (section 4.3). */
  ownNames: Map<string, string>;
  /** Metric target -> accepted value (section 3). */
  metrics: Map<string, string>;
  typography: ShellTypography | null;
  fontFaces: ShellFontFace[];
  layout: EmbedLayout;
}

export type ShellThemeValidation = { ok: false; reason: ShellThemeRejectReason } | AcceptedShellTheme;

function validateTypography(raw: Record<string, unknown>): ShellTypography | null {
  const pick = (v: unknown) => (typeof v === "string" && isFamilyList(v) ? v : null);
  const sansList = pick(raw.sans);
  const headingList = pick(raw.heading);
  const monoList = pick(raw.mono);
  const sans = sansList ? withMeetFallback(sansList, MEET_DEFAULT_SANS) : null;
  const headingSource = raw.heading === undefined ? sansList : headingList;
  const heading = headingSource ? withMeetFallback(headingSource, MEET_DEFAULT_HEADING) : null;
  const mono = monoList ? withMeetFallback(monoList, MEET_DEFAULT_MONO) : null;
  const features = typeof raw.features === "string" && isFeatures(raw.features) ? raw.features : null;
  const smoothing = raw.smoothing === "antialiased" || raw.smoothing === "auto" ? raw.smoothing : null;
  if (!sans && !heading && !mono && !features && !smoothing) return null;
  return { sans, heading, mono, features, smoothing };
}

/**
 * Sections 5.1 to 5.8. `shellOrigin` is required for `fontFaces` (5.7);
 * without it every face is dropped.
 */
export function validateShellTheme(data: unknown, shellOrigin: string | null = null): ShellThemeValidation {
  if (!isPlainObject(data)) return { ok: false, reason: "shape" };
  let serialized: string;
  try {
    serialized = JSON.stringify(data);
  } catch {
    return { ok: false, reason: "shape" };
  }
  if (serialized.length > LIMITS[2].message) {
    return { ok: false, reason: "size" };
  }
  if (data.type !== SHELL_THEME_TYPE) return { ok: false, reason: "type" };
  const version = data.version;
  if (!isSupportedVersion(version)) return { ok: false, reason: "version" };
  const limits = LIMITS[version];
  if (serialized.length > limits.message) return { ok: false, reason: "size" };
  const mode = data.mode;
  if (mode !== "light" && mode !== "dark") {
    return { ok: false, reason: "mode" };
  }
  const tokens = data.tokens;
  if (!isPlainObject(tokens)) return { ok: false, reason: "tokens" };
  const keys = Object.keys(tokens);
  if (keys.length > limits.tokens || !hasOnlyStringValues(tokens)) {
    return { ok: false, reason: "tokens" };
  }

  let rawMetrics: Record<string, unknown> = {};
  let rawTypography: Record<string, unknown> | null = null;
  let rawFaces: unknown[] = [];
  let rawLayout: unknown;
  if (version >= 2) {
    if (data.metrics !== undefined) {
      const m = data.metrics;
      if (!isPlainObject(m) || Object.keys(m).length > limits.metrics || !hasOnlyStringValues(m)) {
        return { ok: false, reason: "metrics" };
      }
      rawMetrics = m;
    }
    if (data.typography !== undefined) {
      const t = data.typography;
      if (!isPlainObject(t) || !hasOnlyStringValues(t)) {
        return { ok: false, reason: "typography" };
      }
      rawTypography = t;
    }
    if (data.fontFaces !== undefined) {
      const f = data.fontFaces;
      if (!Array.isArray(f) || f.length > limits.fontFaces || !f.every(isPlainObject)) {
        return { ok: false, reason: "fontFaces" };
      }
      rawFaces = f;
    }
    if (data.layout !== undefined) {
      if (!isPlainObject(data.layout)) return { ok: false, reason: "layout" };
      rawLayout = data.layout;
    }
  }

  const values = new Map<string, string>();
  for (const target of MEET_TARGETS) {
    const source = SHELL_TO_MEET_MAP[target] as string;
    if (!Object.hasOwn(tokens, source)) continue;
    const value = tokens[source] as string;
    if (isColorValue(value)) values.set(target, value);
  }
  const ownNames = new Map<string, string>();
  for (const name of keys) {
    if (!isOwnNameToken(name)) continue;
    const value = tokens[name] as string;
    if (isColorValue(value)) ownNames.set(name, value);
  }
  const metrics = new Map<string, string>();
  for (const name of METRIC_TARGETS) {
    if (!Object.hasOwn(rawMetrics, name)) continue;
    const value = rawMetrics[name] as string;
    const spec = SHELL_METRICS[name] as MetricSpec;
    const ok = spec.kind === "length" ? isLength(value, spec.min ?? 0, spec.max ?? 0) : isShadow(value);
    if (ok) metrics.set(name, value);
  }
  const fontFaces: ShellFontFace[] = [];
  for (const entry of rawFaces) {
    const face = validateFontFace(entry as Record<string, unknown>, shellOrigin);
    if (face) fontFaces.push(face);
  }

  return {
    ok: true,
    version,
    mode,
    values,
    ownNames,
    metrics,
    typography: rawTypography ? validateTypography(rawTypography) : null,
    fontFaces,
    layout: version >= 2 ? validateLayout(rawLayout) : { ...EMBED_DEFAULT_LAYOUT },
  };
}
